import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const DocumentRetention = () => {
  const [retentionPolicies, setRetentionPolicies] = useState({
    defaultRetentionDays: 2555, // 7 years
    autoArchiveEnabled: true,
    autoArchiveDays: 1095, // 3 years
    autoDeleteEnabled: false,
    autoDeleteDays: 3650, // 10 years
    versionRetentionLimit: 50,
    draftRetentionDays: 90,
    deletedItemRetentionDays: 30
  });

  const [storageSettings, setStorageSettings] = useState({
    compressionEnabled: true,
    compressionLevel: 'medium',
    deduplicationEnabled: true,
    thumbnailGeneration: true,
    fullTextIndexing: true
  });

  const [cleanupSchedule, setCleanupSchedule] = useState({
    frequency: 'weekly',
    dayOfWeek: 'sunday',
    timeOfDay: '02:00',
    notifyAdmins: true,
    generateReports: true
  });

  const [hasChanges, setHasChanges] = useState(false);

  const retentionOptions = [
    { value: 30, label: '30 Days' },
    { value: 90, label: '90 Days' },
    { value: 180, label: '6 Months' },
    { value: 365, label: '1 Year' },
    { value: 1095, label: '3 Years' },
    { value: 1825, label: '5 Years' },
    { value: 2555, label: '7 Years' },
    { value: 3650, label: '10 Years' }
  ];

  const compressionOptions = [
    { value: 'low', label: 'Low (Faster, Larger Files)' },
    { value: 'medium', label: 'Medium (Balanced)' },
    { value: 'high', label: 'High (Slower, Smaller Files)' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const dayOptions = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' }
  ];

  const handleRetentionChange = (field, value) => {
    setRetentionPolicies(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleStorageChange = (field, value) => {
    setStorageSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleScheduleChange = (field, value) => {
    setCleanupSchedule(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const calculateStorageImpact = () => {
    const totalDocs = 15420; // Mock total documents
    const avgDocSize = 2.5; // MB
    const currentStorage = totalDocs * avgDocSize;
    
    let projectedStorage = currentStorage;
    if (storageSettings?.compressionEnabled) {
      const compressionRatio = storageSettings?.compressionLevel === 'high' ? 0.6 : 
                              storageSettings?.compressionLevel === 'medium' ? 0.75 : 0.9;
      projectedStorage *= compressionRatio;
    }
    
    if (storageSettings?.deduplicationEnabled) {
      projectedStorage *= 0.85; // Assume 15% deduplication savings
    }

    return {
      current: currentStorage?.toFixed(1),
      projected: projectedStorage?.toFixed(1),
      savings: (currentStorage - projectedStorage)?.toFixed(1)
    };
  };

  const storageImpact = calculateStorageImpact();

  const handleSave = () => {
    console.log('Saving retention settings:', { retentionPolicies, storageSettings, cleanupSchedule });
    setHasChanges(false);
  };

  const handleReset = () => {
    setRetentionPolicies({
      defaultRetentionDays: 2555,
      autoArchiveEnabled: true,
      autoArchiveDays: 1095,
      autoDeleteEnabled: false,
      autoDeleteDays: 3650,
      versionRetentionLimit: 50,
      draftRetentionDays: 90,
      deletedItemRetentionDays: 30
    });
    setStorageSettings({
      compressionEnabled: true,
      compressionLevel: 'medium',
      deduplicationEnabled: true,
      thumbnailGeneration: true,
      fullTextIndexing: true
    });
    setCleanupSchedule({
      frequency: 'weekly',
      dayOfWeek: 'sunday',
      timeOfDay: '02:00',
      notifyAdmins: true,
      generateReports: true
    });
    setHasChanges(false);
  };

  return (
    <div className="space-y-8">
      {/* Retention Policies Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Archive" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Document Retention Policies</h3>
            <p className="text-sm text-muted-foreground">Configure automatic document lifecycle management</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Default Retention Period"
              description="How long documents are kept before archiving"
              options={retentionOptions}
              value={retentionPolicies?.defaultRetentionDays}
              onChange={(value) => handleRetentionChange('defaultRetentionDays', value)}
            />

            <Input
              label="Version History Limit"
              type="number"
              value={retentionPolicies?.versionRetentionLimit}
              onChange={(e) => handleRetentionChange('versionRetentionLimit', parseInt(e?.target?.value))}
              min={5}
              max={200}
              description="Maximum versions to keep per document"
            />

            <Input
              label="Draft Retention (Days)"
              type="number"
              value={retentionPolicies?.draftRetentionDays}
              onChange={(e) => handleRetentionChange('draftRetentionDays', parseInt(e?.target?.value))}
              min={7}
              max={365}
              description="How long to keep unsaved drafts"
            />
          </div>

          <div className="space-y-4">
            <div>
              <Checkbox
                label="Enable Auto-Archive"
                description="Automatically move old documents to archive storage"
                checked={retentionPolicies?.autoArchiveEnabled}
                onChange={(e) => handleRetentionChange('autoArchiveEnabled', e?.target?.checked)}
              />
              
              {retentionPolicies?.autoArchiveEnabled && (
                <div className="mt-3 ml-6">
                  <Select
                    label="Archive After"
                    options={retentionOptions}
                    value={retentionPolicies?.autoArchiveDays}
                    onChange={(value) => handleRetentionChange('autoArchiveDays', value)}
                  />
                </div>
              )}
            </div>

            <div>
              <Checkbox
                label="Enable Auto-Delete"
                description="Permanently delete documents after retention period"
                checked={retentionPolicies?.autoDeleteEnabled}
                onChange={(e) => handleRetentionChange('autoDeleteEnabled', e?.target?.checked)}
              />
              
              {retentionPolicies?.autoDeleteEnabled && (
                <div className="mt-3 ml-6">
                  <Select
                    label="Delete After"
                    options={retentionOptions}
                    value={retentionPolicies?.autoDeleteDays}
                    onChange={(value) => handleRetentionChange('autoDeleteDays', value)}
                  />
                  <div className="mt-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} className="text-error" />
                      <span className="text-sm font-medium text-error">Warning</span>
                    </div>
                    <p className="text-sm text-error mt-1">
                      Auto-delete permanently removes documents and cannot be undone
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Input
              label="Deleted Items Retention (Days)"
              type="number"
              value={retentionPolicies?.deletedItemRetentionDays}
              onChange={(e) => handleRetentionChange('deletedItemRetentionDays', parseInt(e?.target?.value))}
              min={1}
              max={90}
              description="How long deleted items stay in recycle bin"
            />
          </div>
        </div>
      </div>
      {/* Storage Optimization Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="HardDrive" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Storage Optimization</h3>
            <p className="text-sm text-muted-foreground">Configure storage efficiency and performance settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Checkbox
                label="Enable File Compression"
                description="Compress documents to save storage space"
                checked={storageSettings?.compressionEnabled}
                onChange={(e) => handleStorageChange('compressionEnabled', e?.target?.checked)}
              />
              
              {storageSettings?.compressionEnabled && (
                <div className="mt-3 ml-6">
                  <Select
                    label="Compression Level"
                    options={compressionOptions}
                    value={storageSettings?.compressionLevel}
                    onChange={(value) => handleStorageChange('compressionLevel', value)}
                  />
                </div>
              )}
            </div>

            <Checkbox
              label="Enable Deduplication"
              description="Remove duplicate files to save space"
              checked={storageSettings?.deduplicationEnabled}
              onChange={(e) => handleStorageChange('deduplicationEnabled', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Generate Thumbnails"
              description="Create preview thumbnails for supported file types"
              checked={storageSettings?.thumbnailGeneration}
              onChange={(e) => handleStorageChange('thumbnailGeneration', e?.target?.checked)}
            />

            <Checkbox
              label="Full-Text Indexing"
              description="Index document content for faster searching"
              checked={storageSettings?.fullTextIndexing}
              onChange={(e) => handleStorageChange('fullTextIndexing', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Storage Impact Preview */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Storage Impact Assessment</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Current Storage:</span>
              <p className="font-medium text-foreground">{storageImpact?.current} GB</p>
            </div>
            <div>
              <span className="text-muted-foreground">Projected Storage:</span>
              <p className="font-medium text-foreground">{storageImpact?.projected} GB</p>
            </div>
            <div>
              <span className="text-muted-foreground">Estimated Savings:</span>
              <p className="font-medium text-success">{storageImpact?.savings} GB</p>
            </div>
          </div>
        </div>
      </div>
      {/* Cleanup Schedule Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Automated Cleanup Schedule</h3>
            <p className="text-sm text-muted-foreground">Configure when retention policies are enforced</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Cleanup Frequency"
              options={frequencyOptions}
              value={cleanupSchedule?.frequency}
              onChange={(value) => handleScheduleChange('frequency', value)}
            />

            {cleanupSchedule?.frequency === 'weekly' && (
              <Select
                label="Day of Week"
                options={dayOptions}
                value={cleanupSchedule?.dayOfWeek}
                onChange={(value) => handleScheduleChange('dayOfWeek', value)}
              />
            )}

            <Input
              label="Time of Day"
              type="time"
              value={cleanupSchedule?.timeOfDay}
              onChange={(e) => handleScheduleChange('timeOfDay', e?.target?.value)}
              description="When to run cleanup operations"
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Notify Administrators"
              description="Send email notifications when cleanup runs"
              checked={cleanupSchedule?.notifyAdmins}
              onChange={(e) => handleScheduleChange('notifyAdmins', e?.target?.checked)}
            />

            <Checkbox
              label="Generate Cleanup Reports"
              description="Create detailed reports of cleanup activities"
              checked={cleanupSchedule?.generateReports}
              onChange={(e) => handleScheduleChange('generateReports', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Next Scheduled Cleanup</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Sunday, November 17, 2025 at 2:00 AM EST
          </p>
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">You have unsaved changes</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>
              Reset Changes
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Retention Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentRetention;